import { formatShortDateRange } from "@/lib/date-utils";
import type { AvailabilityStatus } from "@/hooks/useProjectPeople";
import type { PersonType } from "@/hooks/usePeople";

export type CrewPlanningPerson = {
  id: string;
  first_name: string;
  last_name: string;
  person_type?: string | null;
};

export type CrewPlanningProject = {
  id: string;
  name: string;
  planning_start_date?: string | null;
  planning_end_date?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  start_date?: string | null;
  end_date?: string | null;
};

export type CrewPlanningAvailability = {
  id: string;
  project_id: string;
  person_id: string;
  status: AvailabilityStatus;
  available_days?: string[] | null;
  notes?: string | null;
  person?: CrewPlanningPerson | null;
  project?: CrewPlanningProject | null;
};

export type CrewPlanningProjectRow = {
  availability: CrewPlanningAvailability;
  project: CrewPlanningProject;
  dateLabel: string;
  sortDate: string;
};

export type CrewPlanningPersonGroup = {
  person: CrewPlanningPerson;
  rows: CrewPlanningProjectRow[];
};

function toUtcDay(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date.getTime();
}

export function getCrewPlanningProjectRange(project: CrewPlanningProject) {
  const start =
    project.planning_start_date ??
    project.event_start_date ??
    project.start_date ??
    null;
  const end =
    project.planning_end_date ??
    project.event_end_date ??
    project.end_date ??
    start;

  return { start, end };
}

export function isFutureCrewPlanningProject(
  project: CrewPlanningProject,
  todayUtcMs: number,
) {
  const { start, end } = getCrewPlanningProjectRange(project);
  const startMs = toUtcDay(start);
  const endMs = toUtcDay(end) ?? startMs;

  if (startMs === null && endMs === null) return false;
  return (endMs ?? startMs!) >= todayUtcMs;
}

export function getCrewPlanningProjectDateLabel(project: CrewPlanningProject) {
  const { start, end } = getCrewPlanningProjectRange(project);
  return formatShortDateRange(start, end) || "No date";
}

export function groupCrewPlanningAvailability({
  availability,
  selectedStatuses,
  selectedPersonTypes,
  todayUtcMs,
}: {
  availability: CrewPlanningAvailability[];
  selectedStatuses: AvailabilityStatus[];
  selectedPersonTypes?: PersonType[];
  todayUtcMs: number;
}): CrewPlanningPersonGroup[] {
  if (selectedStatuses.length === 0) return [];
  const selected = new Set(selectedStatuses);
  const selectedTypes = selectedPersonTypes
    ? new Set<PersonType>(selectedPersonTypes)
    : null;
  const groups = new Map<string, CrewPlanningPersonGroup>();

  for (const row of availability) {
    if (!selected.has(row.status) || !row.person || !row.project) continue;
    if (
      selectedTypes &&
      (!row.person.person_type ||
        !selectedTypes.has(row.person.person_type as PersonType))
    ) {
      continue;
    }
    if (!isFutureCrewPlanningProject(row.project, todayUtcMs)) continue;

    const group =
      groups.get(row.person.id) ??
      ({
        person: row.person,
        rows: [],
      } satisfies CrewPlanningPersonGroup);

    group.rows.push({
      availability: row,
      project: row.project,
      dateLabel: getCrewPlanningProjectDateLabel(row.project),
      sortDate: getCrewPlanningProjectRange(row.project).start ?? "",
    });
    groups.set(row.person.id, group);
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      rows: group.rows.sort((a, b) => a.sortDate.localeCompare(b.sortDate)),
    }))
    .sort((a, b) => {
      const last = (a.person.last_name ?? "").localeCompare(
        b.person.last_name ?? "",
      );
      if (last !== 0) return last;
      return (a.person.first_name ?? "").localeCompare(
        b.person.first_name ?? "",
      );
    });
}
